import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  alpha,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Popover,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Calculate as CalculateIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  TableChart as TableChartIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore
} from '@mui/icons-material';
import api from '@services/api';
import { API_CONFIG } from '../config/api';
import { useDataSync, createCacheBustingUrl, type DataSyncEvent } from '../utils/dataSync';

// Import our new components
import ValuationMethodsPanel from 'features/valuation/components/ValuationMethodsPanel';
import ValuationAssumptionsPanel from 'features/valuation/components/ValuationAssumptionsPanel';
import ValuationResultsPanel from 'features/valuation/components/ValuationResultsPanel';
import ValuationSummaryPanel from 'features/valuation/components/ValuationSummaryPanel';
import FinancialDataOverview from 'features/valuation/components/FinancialDataOverview';

// Types
interface ValuationData {
  methods: Record<string, ValuationMethod>;
  summary: ValuationSummary;
  assumptions: ValuationAssumptions;
  financialData: any;
  dataQuality: DataQuality;
}

interface ValuationMethod {
  name: string;
  value: number;
  confidence: 'high' | 'medium' | 'low';
  details: any;
  metadata?: {
    name: string;
    description: string;
    formula: string;
    bestFor: string[];
    limitations: string[];
    parameters: Record<string, any>;
  };
}

interface ValuationSummary {
  weightedValue: number;
  aiEnhancedValue: number;
  valueRange: { low: number; high: number };
  confidence: string;
  keyDrivers: string[];
  recommendations: string[];
}

interface ValuationAssumptions {
  capRate: { rate: number; noi: number };
  revenueMultiple: { multiple: number; revenue: number };
  dcf: { growthRate: number; discountRate: number; projectionYears: number };
  salesComparison: { pricePerRoom: number; numRooms: number };
  replacementCost: { costPerSqFt: number; depreciation: number; sqFt: number };
}

interface DataQuality {
  score: number;
  level: 'high' | 'medium' | 'low';
  missing: string[];
  available: string[];
}

const ValuationAnalysisPage: React.FC = () => {
  const { hotelId = '' } = useParams<{ hotelId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // State management
  const [currentTab, setCurrentTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confidenceAnchorEl, setConfidenceAnchorEl] = useState<HTMLElement | null>(null);
  const [aiValueAnchorEl, setAiValueAnchorEl] = useState<HTMLElement | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [hotelName, setHotelName] = useState<string>('');
  const [valuationData, setValuationData] = useState<ValuationData | null>(null);
  const [yearRange, setYearRange] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [customAssumptions, setCustomAssumptions] = useState<Partial<ValuationAssumptions>>({});
  const [lastDataFetch, setLastDataFetch] = useState<number>(0);

    // Setup data sync to refresh when P&L data changes
  const { notifyUpdate } = useDataSync(hotelId, (event: DataSyncEvent) => {
    console.log('ValuationAnalysisPage: Received data sync event:', event);

    if (event.type === 'FINANCIAL_DATA_UPDATED' && event.source !== 'ValuationAnalysisPage') {
      console.log('ValuationAnalysisPage: Financial data updated elsewhere, refreshing...');
      setRefreshing(true);
      fetchHotelData(); // Force refresh
      // Also notify that valuation should be updated since financial data changed
      setTimeout(() => {
        notifyUpdate('VALUATION_DATA_UPDATED', 'ValuationAnalysisPage');
        setRefreshing(false);
      }, 1000);
    }

    if (event.type === 'VALUATION_DATA_UPDATED' && event.source !== 'ValuationAnalysisPage') {
      console.log('ValuationAnalysisPage: Valuation data updated elsewhere, refreshing...');
      setRefreshing(true);
      fetchHotelData(); // Force refresh
      setTimeout(() => setRefreshing(false), 1000);
    }
  });

  // Utility functions - memoized for performance
  const calculateDataQuality = useCallback((financialData: any): DataQuality => {
    if (!financialData) {
      return {
        score: 0,
        level: 'low' as const,
        missing: ['All financial data'],
        available: []
      };
    }

    const available: string[] = [];
    const missing: string[] = [];

    // Check for different types of financial data
    if (financialData.confirmedFinancials?.length > 0) available.push('Confirmed Financials');
    else missing.push('Confirmed Financials');

    if (financialData.hotelMetrics?.length > 0) available.push('Hotel Metrics');
    else missing.push('Hotel Metrics');

    if (financialData.profitLoss?.length > 0) available.push('Profit & Loss Data');
    else missing.push('Profit & Loss Data');

    if (financialData.renovationHistory?.length > 0) available.push('Renovation History');
    else missing.push('Renovation History');

    if (financialData.marketResearch?.length > 0) available.push('Market Research');
    else missing.push('Market Research');

    const score = Math.round((available.length / (available.length + missing.length)) * 100);
    const level: 'high' | 'medium' | 'low' = score >= 80 ? 'high' : score >= 50 ? 'medium' : 'low';

    return { score, level, available, missing };
  }, []);

  const extractAssumptions = useCallback((methods: Record<string, ValuationMethod>): ValuationAssumptions => {
    const defaults: ValuationAssumptions = {
      capRate: { rate: 0.08, noi: 0 },
      revenueMultiple: { multiple: 3.5, revenue: 0 },
      dcf: { growthRate: 0.02, discountRate: 0.12, projectionYears: 5 },
      salesComparison: { pricePerRoom: 150000, numRooms: 0 },
      replacementCost: { costPerSqFt: 350, depreciation: 0.2, sqFt: 0 }
    };

    if (!methods) return defaults;

    return {
      capRate: {
        rate: methods.capRate?.details?.capRate || defaults.capRate.rate,
        noi: methods.capRate?.details?.noi || defaults.capRate.noi
      },
      revenueMultiple: {
        multiple: methods.revenueMultiple?.details?.multiple || defaults.revenueMultiple.multiple,
        revenue: methods.revenueMultiple?.details?.revenue || defaults.revenueMultiple.revenue
      },
      dcf: {
        growthRate: methods.dcf?.details?.growthRate || defaults.dcf.growthRate,
        discountRate: methods.dcf?.details?.discountRate || defaults.dcf.discountRate,
        projectionYears: methods.dcf?.details?.projectionYears || defaults.dcf.projectionYears
      },
      salesComparison: {
        pricePerRoom: methods.salesComparison?.details?.pricePerRoom || defaults.salesComparison.pricePerRoom,
        numRooms: methods.salesComparison?.details?.numRooms || defaults.salesComparison.numRooms
      },
      replacementCost: {
        costPerSqFt: methods.replacementCost?.details?.costPerSqFt || defaults.replacementCost.costPerSqFt,
        depreciation: methods.replacementCost?.details?.depreciation || defaults.replacementCost.depreciation,
        sqFt: methods.replacementCost?.details?.sqFt || defaults.replacementCost.sqFt
      }
    };
  }, []);

  // Extract available years - memoized
  const extractAvailableYears = useCallback((comprehensiveData: any): number[] => {
    const years = new Set<number>();

    console.log('🔍 Extracting years from comprehensive data:', comprehensiveData);

    // Extract years from confirmed financials (aggregated data)
    if (comprehensiveData.financialData?.confirmedFinancials) {
      comprehensiveData.financialData.confirmedFinancials.forEach((item: any) => {
        if (item.year) {
          const year = parseInt(item.year);
          if (!isNaN(year)) {
            years.add(year);
            console.log(`✅ Found year from confirmedFinancials: ${year}`);
          }
        }
      });
    }

    // Extract years from profit loss data (legacy data)
    if (comprehensiveData.financialData?.profitLoss) {
      comprehensiveData.financialData.profitLoss.forEach((item: any) => {
        if (item.year) {
          const year = parseInt(item.year);
          if (!isNaN(year)) {
            years.add(year);
            console.log(`✅ Found year from profitLoss: ${year}`);
          }
        }
      });
    }

    // Extract years from hotel metrics
    if (comprehensiveData.financialData?.hotelMetrics) {
      comprehensiveData.financialData.hotelMetrics.forEach((item: any) => {
        if (item.year) {
          const year = parseInt(item.year);
          if (!isNaN(year)) {
            years.add(year);
            console.log(`✅ Found year from hotelMetrics: ${year}`);
          }
        }
      });
    }

    // Extract years from raw OCR data (monthly data that's been aggregated)
    if (comprehensiveData.financialData?.ocrData) {
      comprehensiveData.financialData.ocrData.forEach((item: any) => {
        if (item.year) {
          const year = parseInt(item.year);
          if (!isNaN(year)) {
            years.add(year);
            console.log(`✅ Found year from ocrData: ${year}`);
          }
        }
      });
    }

    const yearArray = Array.from(years).sort((a, b) => a - b);
    console.log(`📅 Extracted ${yearArray.length} unique years:`, yearArray);

    // If we don't have any years, try to extract from current year as a fallback
    if (yearArray.length === 0) {
      const currentYear = new Date().getFullYear();
      const fallbackStart = currentYear - 2;
      const fallbackEnd = currentYear;
      console.log(`⚠️ No years found in data, using fallback range: ${fallbackStart}-${fallbackEnd}`);
      return [fallbackStart, fallbackEnd];
    }

    return yearArray;
  }, []);

  // Helper function that accepts year range as parameter to avoid race conditions
  const generateValuationWithYearRange = useCallback(async (
    financialData?: any,
    customParams?: Partial<ValuationAssumptions>,
    comprehensiveData?: any,
    targetYearRange?: { start: number; end: number }
  ) => {
    const effectiveYearRange = targetYearRange || yearRange;

    // Enhanced validation with detailed logging
    if (!hotelId) {
      console.error('❌ generateValuationWithYearRange: No hotelId provided');
      setError('Hotel ID is required for valuation generation.');
      return;
    }

    if (effectiveYearRange.start === 0 || effectiveYearRange.end === 0) {
      console.error('❌ generateValuationWithYearRange: Invalid year range:', effectiveYearRange);
      setError(`Invalid year range: ${effectiveYearRange.start}-${effectiveYearRange.end}. Please ensure valid years are selected.`);
      return;
    }

    if (effectiveYearRange.start > effectiveYearRange.end) {
      console.error('❌ generateValuationWithYearRange: Start year is after end year:', effectiveYearRange);
      setError(`Invalid year range: Start year (${effectiveYearRange.start}) cannot be after end year (${effectiveYearRange.end}).`);
      return;
    }

    console.log('✅ generateValuationWithYearRange: Starting with valid parameters:', {
      hotelId,
      yearRange: effectiveYearRange,
      hasFinancialData: !!financialData,
      hasComprehensiveData: !!comprehensiveData
    });

    setLoading(true);
    setError(null);

    try {
      const options = {
        startYear: effectiveYearRange.start,
        endYear: effectiveYearRange.end,
        methodParameters: customParams || customAssumptions
      };

      console.log('🔧 Generating valuation with options:', options);

      const valuationUrl = `${API_CONFIG.BASE_URL}/api/enhanced-valuation/${hotelId}/compare`;
      console.log('Valuation URL:', valuationUrl);

      // Check token again before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required for valuation generation.');
        return;
      }

      const response = await fetch(valuationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(options)
      });

      console.log('Valuation response status:', response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Valuation API Error:', errorData);

        if (response.status === 401 || response.status === 403) {
          setError('Authentication expired during valuation. Please log in again.');
          localStorage.removeItem('token');
          setTimeout(() => navigate('/login'), 2000);
          return;
        } else if (response.status === 404) {
          setError('Valuation service not found. Please contact support.');
          return;
        } else if (response.status === 400) {
          try {
            const errorJson = JSON.parse(errorData);
            setError(errorJson.message || 'Invalid data for valuation calculation.');
          } catch {
            setError('Invalid data for valuation calculation. Please check your financial data.');
          }
          return;
        } else {
          setError(`Valuation service error (${response.status}): ${errorData || 'Failed to generate valuation'}`);
          return;
        }
      }

      const data = await response.json();
      console.log('Valuation data received:', Object.keys(data));

      // Validate response structure
      if (!data.comparison || !data.comparison.methods) {
        console.error('Invalid valuation response structure:', data);
        setError('Invalid response from valuation service. Please try again.');
        return;
      }

      // Transform the data into our expected format
      const transformedData: ValuationData = {
        methods: data.comparison.methods || {},
        summary: {
          weightedValue: data.comparison.methods?.weightedAverage?.value || 0,
          aiEnhancedValue: data.comparison.methods?.aiEnhanced?.value || 0,
          valueRange: {
            low: (data.comparison.methods?.weightedAverage?.value || 0) * 0.85,
            high: (data.comparison.methods?.weightedAverage?.value || 0) * 1.15
          },
          confidence: data.comparison.methods?.weightedAverage?.confidence || 'medium',
          keyDrivers: data.comparison.summary?.recommendations || [],
          recommendations: data.comparison.summary?.recommendations || []
        },
        assumptions: extractAssumptions(data.comparison.methods),
        // Use comprehensive financial data if available, otherwise use the data from valuation response
        financialData: comprehensiveData?.financialData || financialData || data.financialData,
        dataQuality: data.comparison.summary?.dataQuality || calculateDataQuality(comprehensiveData?.financialData)
      };

      setValuationData(transformedData);

      // Save the valuation results to update hotel record
      console.log('💾 Saving valuation results to update hotel estimated_value...');
      try {
        const saveUrl = `${API_CONFIG.BASE_URL}/api/enhanced-valuation/${hotelId}`;
        const saveResponse = await fetch(saveUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(options)
        });

        if (saveResponse.ok) {
          console.log('✅ Valuation results saved successfully, hotel estimated_value updated');
          // Notify other components that valuation data has been updated
          notifyUpdate('VALUATION_DATA_UPDATED', 'ValuationAnalysisPage');
        } else {
          console.warn('⚠️ Failed to save valuation results, hotel estimated_value not updated');
        }
      } catch (saveError) {
        console.warn('⚠️ Error saving valuation results:', saveError);
      }

      setSuccess('Valuation completed successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err: any) {
      console.error('Valuation generation error:', err);

      if (err.name === 'AbortError') {
        setError('Valuation request timed out. Please try again.');
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setError('Network error during valuation. Please check your connection.');
      } else if (err.message?.includes('JSON')) {
        setError('Invalid response from valuation service. Please try again.');
      } else {
        setError(err.message || 'Failed to generate valuation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [hotelId, yearRange, customAssumptions, navigate, extractAssumptions, calculateDataQuality]);

  // Memoize the comprehensive financial data fetch
  const fetchHotelData = useCallback(async () => {
    if (!hotelId) return;

    setLoading(true);
    setError(null);

    try {
      // Add diagnostics first
      console.log('🔍 Diagnosing valuation dashboard issue...');
      console.log('Hotel ID:', hotelId);
      console.log('API Base URL:', API_CONFIG.BASE_URL);
      console.log('Token exists:', !!localStorage.getItem('token'));

      // Test basic connectivity first
      const healthCheckUrl = `${API_CONFIG.BASE_URL}/health`;
      console.log('Testing connectivity to:', healthCheckUrl);

      try {
        const healthResponse = await fetch(healthCheckUrl, {
          method: 'GET'
        });
        console.log('Health check status:', healthResponse.status);
      } catch (healthError: any) {
        console.error('❌ Health check failed - backend may not be running:', healthError.message);
        setError('Backend server is not responding. Please ensure the backend is running on port 5000.');
        return;
      }

      // Check authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Fetch comprehensive financial data first with aggressive cache busting
      const cacheBuster = `?_t=${Date.now()}&r=${Math.random()}`;
      const financialUrl = `${API_CONFIG.BASE_URL}/api/hotels/${hotelId}/comprehensive-financials${cacheBuster}`;

      console.log('ValuationAnalysisPage: Starting fetch request...');
      console.log('URL:', financialUrl);

      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('ValuationAnalysisPage: Request timed out after 15 seconds, aborting...');
        controller.abort();
      }, 15000); // Reduced to 15 second timeout

      const financialResponse = await fetch(financialUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId); // Clear timeout if request succeeds
      console.log('ValuationAnalysisPage: Response received, status:', financialResponse.status);

      if (!financialResponse.ok) {
        if (financialResponse.status === 401 || financialResponse.status === 403) {
          setError('Authentication expired. Please log in again.');
          localStorage.removeItem('token');
          setTimeout(() => navigate('/login'), 2000);
          return;
        } else if (financialResponse.status === 404) {
          setError(`Hotel with ID ${hotelId} not found. Please check the URL or contact support.`);
          return;
        } else {
          const errorText = await financialResponse.text();
          console.error('API Error Response:', errorText);
          setError(`Server error (${financialResponse.status}): ${errorText || 'Failed to fetch financial data'}`);
          return;
        }
      }

      console.log('ValuationAnalysisPage: Parsing JSON response...');
      const comprehensiveData = await financialResponse.json();
      console.log('ValuationAnalysisPage: JSON parsed successfully, records:', {
        confirmedFinancials: comprehensiveData.financialData?.confirmedFinancials?.length || 0,
        hotelMetrics: comprehensiveData.financialData?.hotelMetrics?.length || 0,
        profitLoss: comprehensiveData.financialData?.profitLoss?.length || 0
      });

      // Set hotel name from the response
      if (comprehensiveData.hotel?.name) {
        setHotelName(comprehensiveData.hotel.name);
      } else {
        setHotelName(`Hotel ${hotelId.substring(0, 8)}`);
      }

      console.log('ValuationAnalysisPage: Extracting available years...');
      // Extract available years and determine year range
      const availableYears = extractAvailableYears(comprehensiveData);
      console.log('ValuationAnalysisPage: Available years:', availableYears);

      let currentYearRange = yearRange;
      if (availableYears.length > 0) {
        if (yearRange.start === 0 && yearRange.end === 0) {
          // Auto-set year range from available data
          const startYear = availableYears[0]; // Earliest year
          const endYear = availableYears[availableYears.length - 1]; // Latest year
          currentYearRange = { start: startYear, end: endYear };
          setYearRange(currentYearRange);
          console.log('✅ ValuationAnalysisPage: Auto-set year range:', currentYearRange);
        } else {
          // Validate existing year range against available data
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];

          if (yearRange.start < minYear || yearRange.end > maxYear) {
            console.log('⚠️ Current year range is invalid, adjusting to fit available data');
            currentYearRange = {
              start: Math.max(yearRange.start, minYear),
              end: Math.min(yearRange.end, maxYear)
            };
            setYearRange(currentYearRange);
            console.log('✅ Adjusted year range:', currentYearRange);
          } else {
            currentYearRange = yearRange;
            console.log('✅ Using existing valid year range:', currentYearRange);
          }
        }
      } else {
        // No years found in financial data - use current year as fallback
        const currentYear = new Date().getFullYear();
        currentYearRange = { start: currentYear - 2, end: currentYear };
        setYearRange(currentYearRange);
        console.log('⚠️ No years in financial data, using fallback range:', currentYearRange);
      }

      // Check if we have sufficient data before attempting valuation
      const totalFinancialRecords = (comprehensiveData.financialData?.confirmedFinancials?.length || 0) +
                                  (comprehensiveData.financialData?.hotelMetrics?.length || 0) +
                                  (comprehensiveData.financialData?.profitLoss?.length || 0);

      if (totalFinancialRecords === 0) {
        console.log('ValuationAnalysisPage: No financial data found, showing empty state');
        // Set empty valuation data to show the interface
        const emptyValuationData: ValuationData = {
          methods: {},
          summary: {
            weightedValue: 0,
            aiEnhancedValue: 0,
            valueRange: { low: 0, high: 0 },
            confidence: 'low',
            keyDrivers: [],
            recommendations: ['Upload financial statements to begin valuation analysis']
          },
          assumptions: {
            capRate: { rate: 0.08, noi: 0 },
            revenueMultiple: { multiple: 3.5, revenue: 0 },
            dcf: { growthRate: 0.02, discountRate: 0.12, projectionYears: 5 },
            salesComparison: { pricePerRoom: 150000, numRooms: 0 },
            replacementCost: { costPerSqFt: 350, depreciation: 0.2, sqFt: 0 }
          },
          financialData: comprehensiveData.financialData,
          dataQuality: calculateDataQuality(comprehensiveData.financialData)
        };
        setValuationData(emptyValuationData);
        setError('No financial data available. Please upload profit & loss statements to begin valuation analysis.');
        return;
      }

      // Always generate valuation with the fresh comprehensive financial data
      // This ensures the valuation reflects the latest profit & loss data
      if (currentYearRange.start > 0 && currentYearRange.end > 0 && currentYearRange.start <= currentYearRange.end) {
        console.log('✅ ValuationAnalysisPage: Starting valuation generation with range:', currentYearRange);
        console.log('📊 Financial data summary:', {
          confirmedFinancials: comprehensiveData.financialData?.confirmedFinancials?.length || 0,
          hotelMetrics: comprehensiveData.financialData?.hotelMetrics?.length || 0,
          profitLoss: comprehensiveData.financialData?.profitLoss?.length || 0,
          ocrData: comprehensiveData.financialData?.ocrData?.length || 0
        });

        await generateValuationWithYearRange(comprehensiveData.financialData, undefined, comprehensiveData, currentYearRange);
        console.log('✅ ValuationAnalysisPage: Valuation generation completed successfully');
      } else {
        console.error('❌ ValuationAnalysisPage: Invalid year range for valuation generation:', {
          currentYearRange,
          availableYears,
          yearRangeValid: currentYearRange.start > 0 && currentYearRange.end > 0 && currentYearRange.start <= currentYearRange.end
        });
        setError(`Invalid analysis period: ${currentYearRange.start}-${currentYearRange.end}. Please ensure your financial data includes valid year information. Available years: ${availableYears.join(', ')}`);
      }

    } catch (err: any) {
      console.error('ValuationAnalysisPage: Fetch error:', err);

      if (err.name === 'AbortError') {
        setError('Request timed out. The server may be experiencing heavy load. Please try again.');
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setError('Network connection error. Please check your internet connection and ensure the backend server is running on port 5000.');
      } else if (err.message?.includes('JSON')) {
        setError('Server returned invalid data. Please contact support if this persists.');
      } else {
        setError(err.message || 'An unexpected error occurred while loading hotel data.');
      }
    } finally {
      setLoading(false);
    }
  }, [hotelId, navigate, extractAvailableYears, calculateDataQuality, generateValuationWithYearRange]);

  // Debounced valuation generation to prevent multiple rapid calls
  const generateValuation = useCallback(async (financialData?: any, customParams?: Partial<ValuationAssumptions>, comprehensiveData?: any) => {
    if (!hotelId || yearRange.start === 0 || yearRange.end === 0) {
      return;
    }

    return generateValuationWithYearRange(financialData, customParams, comprehensiveData, yearRange);
  }, [hotelId, yearRange.start, yearRange.end, customAssumptions]);

  // Enhanced refresh function that forces valuation regeneration
  const handleRefreshData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    setSuccess(null);

    try {
      // CLEAR ALL EXISTING STATE to force fresh data processing
      console.log('ValuationAnalysisPage: Clearing existing state for fresh refresh...');
      setValuationData(null);
      setLoading(true);

      // Step 1: Sync all financial data to ensure confirmed_financials is up to date
      try {
        console.log('ValuationAnalysisPage: Syncing financial data before refresh...');
          await api.syncFinancialData(hotelId);
          console.log('ValuationAnalysisPage: Financial data sync completed');
        } catch (syncError: any) {
          console.warn('ValuationAnalysisPage: Financial sync failed (non-critical):', syncError.message);
          // Continue even if sync fails
      }

      // Step 2: Force a fresh fetch of financial data and regenerate valuation
      console.log('ValuationAnalysisPage: Fetching fresh financial data...');
      await fetchHotelData();

      setSuccess('✅ Valuation refreshed successfully with latest profit & loss data');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh data');
      setLoading(false); // Ensure loading state is cleared on error
    } finally {
      setRefreshing(false);
    }
  }, [fetchHotelData, hotelId]);

  // Debounced recalculation function
  const handleRecalculate = useCallback(async () => {
    if (valuationData?.financialData) {
      await generateValuationWithYearRange(valuationData.financialData, customAssumptions, { financialData: valuationData.financialData });
    }
  }, [generateValuationWithYearRange, valuationData?.financialData, customAssumptions]);

  // OPTIMIZED useEffect hooks with proper dependencies and debouncing

  // Initial data fetch - ALWAYS fetch fresh data when navigating to this page
  useEffect(() => {
    if (hotelId) {
      // Always fetch fresh data to ensure database changes are reflected
      console.log('ValuationAnalysisPage: Fetching fresh data for hotel:', hotelId);
      fetchHotelData();
    }
  }, [hotelId, fetchHotelData]); // Include fetchHotelData to ensure fresh data on navigation

  // REMOVED the auto-refresh on visibility change and focus events to prevent excessive API calls
  // But we DO want to fetch fresh data every time the user navigates to this page

  // Debounced year range change effect - only recalculate after user stops changing values
  useEffect(() => {
    if (!valuationData || yearRange.start === 0 || yearRange.end === 0) {
      return;
    }

    // Debounce the valuation generation to prevent excessive API calls
    const timeoutId = setTimeout(() => {
      generateValuationWithYearRange(valuationData.financialData, customAssumptions, { financialData: valuationData.financialData });
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [yearRange.start, yearRange.end]); // Only depend on year range values

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const handleAssumptionChange = (method: string, parameter: string, value: number) => {
    setCustomAssumptions(prev => ({
      ...prev,
      [method as keyof ValuationAssumptions]: {
        ...prev[method as keyof ValuationAssumptions],
        [parameter]: value
      }
    }));
  };

  const handleBack = () => {
    navigate(`/hotels/${hotelId}`);
  };

  // Debug function to test connectivity
  const testConnectivity = async () => {
    const results: string[] = [];

    try {
      // Test 1: Health check
      results.push('🔍 Testing backend health...');
      try {
        const healthResponse = await api.get('/health');
        const healthData = healthResponse.data;
        results.push(`✅ Backend is healthy (uptime: ${Math.round(healthData.uptime)}s)`);
      } catch (healthError: any) {
        results.push(`❌ Health check failed: ${healthError.response?.status || 'Network error'}`);
      }

      // Test 2: API test
      results.push('🔍 Testing API endpoint...');
      try {
        const apiResponse = await api.get('/test');
        const apiData = apiResponse.data;
        results.push(`✅ API is working (auth: ${apiData.authenticated})`);
      } catch (apiError: any) {
        results.push(`❌ API test failed: ${apiError.response?.status || 'Network error'}`);
      }

      // Test 3: Hotel endpoint
      results.push('🔍 Testing hotel data endpoint...');
      try {
        const hotelResponse = await api.get(`/hotels/${hotelId}/comprehensive-financials`);
        results.push(`✅ Hotel data endpoint is accessible`);
      } catch (hotelError: any) {
        results.push(`❌ Hotel data failed: ${hotelError.response?.status || 'Network error'}`);
      }

    } catch (error: any) {
      results.push(`❌ Connection test failed: ${error.message}`);
    }

    alert(results.join('\n'));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getDataQualityColor = (level: string) => {
    switch (level) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (!valuationData) return 'pending';

    switch (stepIndex) {
      case 0: // Financial Data Review
        return valuationData.dataQuality.level === 'high' ? 'completed' :
               valuationData.dataQuality.level === 'medium' ? 'warning' : 'error';
      case 1: // Valuation Methods
        return Object.keys(valuationData.methods).length > 0 ? 'completed' : 'pending';
      case 2: // Assumptions
        return valuationData.assumptions ? 'completed' : 'pending';
      case 3: // Results
        return valuationData.summary.weightedValue > 0 ? 'completed' : 'pending';
      default:
        return 'pending';
    }
  };

  const getStepIcon = (stepIndex: number, step: any) => {
    const status = getStepStatus(stepIndex);
    const isActive = activeStep === stepIndex;

    let bgcolor, color;
    switch (status) {
      case 'completed':
        bgcolor = theme.palette.success.main;
        color = 'white';
        break;
      case 'warning':
        bgcolor = theme.palette.warning.main;
        color = 'white';
        break;
      case 'error':
        bgcolor = theme.palette.error.main;
        color = 'white';
        break;
      default:
        bgcolor = isActive ? theme.palette.primary.main : theme.palette.grey[300];
        color = isActive ? 'white' : theme.palette.grey[600];
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor,
          color,
          cursor: 'pointer'
        }}
        onClick={() => handleStepChange(stepIndex)}
      >
        {status === 'completed' ? <CheckCircleIcon fontSize="small" /> : step.icon}
      </Box>
    );
  };

  const steps = [
    {
      label: 'Financial Data Review',
      description: 'Review and validate financial data',
      icon: <AnalyticsIcon fontSize="small" />
    },
    {
      label: 'Valuation Methods',
      description: 'Configure valuation approaches',
      icon: <CalculateIcon fontSize="small" />
    },
    {
      label: 'Assumptions & Parameters',
      description: 'Adjust valuation assumptions',
      icon: <TrendingUpIcon fontSize="small" />
    },
    {
      label: 'Results & Analysis',
      description: 'Review valuation results',
      icon: <InsightsIcon fontSize="small" />
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Loading Valuation Analysis...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fetching financial data and generating valuations
          </Typography>
        </Box>

        {/* Diagnostics Help Section */}
        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            🔍 Troubleshooting Loading Issues
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If the loading takes more than 30 seconds, please check:
          </Typography>

          <Box component="ol" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Backend Server:</strong> Ensure the backend is running on port 5000
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Internet Connection:</strong> Check your network connectivity
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Authentication:</strong> You may need to log in again
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Financial Data:</strong> Upload profit & loss statements for this hotel
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/hotels/${hotelId}`)}
            >
              Back to Hotel
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" fontWeight={700} sx={{ mb: 1 }}>
              Hotel Valuation Analysis
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {hotelName} • Comprehensive Property Valuation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Refresh valuation data to include latest profit & loss updates" arrow>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshData}
                disabled={refreshing || loading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4
                  }
                }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              size="large"
              sx={{ borderRadius: 2 }}
            >
              Back to Hotel
            </Button>
          </Box>
        </Box>

        {/* Compact Quick Stats - Less Vertical Space */}
        {valuationData && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Primary Valuation - Compact */}
            <Grid item xs={12} md={8}>
              <Card
                elevation={2}
                sx={{
                  border: `1px solid ${theme.palette.primary.main}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
                  position: 'relative'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssessmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                        Primary Valuation
                      </Typography>
                    </Box>
                    <Tooltip title="Learn how AI Enhanced Value is calculated">
                      <IconButton
                        size="small"
                        onClick={(e) => setAiValueAnchorEl(e.currentTarget)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {formatCurrency(valuationData.summary.aiEnhancedValue)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI Enhanced
                    </Typography>
                    <Chip
                      label={valuationData.summary.confidence.toUpperCase()}
                      color={valuationData.summary.confidence === 'high' ? 'success' : valuationData.summary.confidence === 'medium' ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Weighted Average
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(valuationData.summary.weightedValue)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Value Range
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(valuationData.summary.valueRange.low)} - {formatCurrency(valuationData.summary.valueRange.high)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Summary Stats - Compact */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Analysis Summary
                  </Typography>

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {Object.keys(valuationData.methods).length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Methods
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {valuationData.dataQuality?.score || 95}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Quality
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color="warning.main">
                          5
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Years
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Confidence Level
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={valuationData.summary.confidence === 'high' ? 85 : valuationData.summary.confidence === 'medium' ? 65 : 35}
                      color={valuationData.summary.confidence === 'high' ? 'success' : valuationData.summary.confidence === 'medium' ? 'warning' : 'error'}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* AI Enhanced Value Explanation Popover */}
        <Popover
          open={Boolean(aiValueAnchorEl)}
          anchorEl={aiValueAnchorEl}
          onClose={() => setAiValueAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, maxWidth: 350 }}>
            <Typography variant="subtitle2" gutterBottom>
              AI Enhanced Valuation Methodology
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Our AI algorithm considers multiple factors:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ ml: 2, mb: 1 }}>
              <li>Market comparables and trends</li>
              <li>Financial performance patterns</li>
              <li>Location and property characteristics</li>
              <li>Economic indicators and forecasts</li>
              <li>Historical valuation accuracy</li>
            </Typography>
            <Typography variant="body2">
              The AI Enhanced Value represents our most comprehensive valuation estimate,
              incorporating machine learning insights alongside traditional methods.
            </Typography>
          </Box>
        </Popover>

        {/* Confidence Level Explanation Popover */}
        <Popover
          open={Boolean(confidenceAnchorEl)}
          anchorEl={confidenceAnchorEl}
          onClose={() => setConfidenceAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              Confidence Level Calculation
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Confidence is determined by:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ ml: 2, mb: 1 }}>
              <li>Data quality and completeness</li>
              <li>Number of valuation methods used</li>
              <li>Convergence between methods</li>
              <li>Market data availability</li>
              <li>Property-specific information</li>
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong style={{ color: theme.palette.success.main }}>High (80-100%):</strong> Comprehensive data, multiple methods align
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong style={{ color: theme.palette.warning.main }}>Medium (50-79%):</strong> Good data, some method variation
              </Typography>
              <Typography variant="body2">
                <strong style={{ color: theme.palette.error.main }}>Low (0-49%):</strong> Limited data, significant uncertainty
              </Typography>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Enhanced Stepper Navigation with Status */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon />
              Analysis Progress
            </Typography>

            {/* Overall Progress Indicator */}
            {valuationData && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Overall Completion
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(steps.filter((_, i) => getStepStatus(i) === 'completed').length / steps.length) * 100}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {steps.filter((_, i) => getStepStatus(i) === 'completed').length} of {steps.length} steps completed
                </Typography>
              </Box>
            )}

            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => {
                const status = getStepStatus(index);
                return (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={() => getStepIcon(index, step)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="subtitle2">{step.label}</Typography>
                        {status === 'completed' && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        {status === 'warning' && (
                          <WarningIcon color="warning" fontSize="small" />
                        )}
                        {status === 'error' && (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {step.description}
                      </Typography>
                      {status === 'completed' && (
                        <Chip
                          label="Completed"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {status === 'warning' && (
                        <Chip
                          label="Needs Review"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {status === 'error' && (
                        <Chip
                          label="Issues Found"
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>

            {/* Quick Action Buttons */}
            {valuationData && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AssessmentIcon />}
                    onClick={() => setCurrentTab(5)}
                    fullWidth
                  >
                    View Summary
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Enhanced Content Panel with Dashboard Tab */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ minHeight: 600 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Dashboard" icon={<TableChartIcon />} />
              <Tab label="Financial Data" icon={<AnalyticsIcon />} />
              <Tab label="Valuation Methods" icon={<CalculateIcon />} />
              <Tab label="Assumptions" icon={<TrendingUpIcon />} />
              <Tab label="Results" icon={<AssessmentIcon />} />
              <Tab label="Summary" icon={<InsightsIcon />} />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {currentTab === 0 && (
                <Box>
                  {/* Streamlined Dashboard Content */}
                  {valuationData ? (
                    <Grid container spacing={3}>
                      {/* Main Analysis Card */}
                      <Grid item xs={12} lg={8}>
                        <Paper sx={{ p: 3, height: 'fit-content' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>
                              Valuation Analysis Complete
                            </Typography>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Analysis Complete"
                              color="success"
                              variant="outlined"
                            />
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Your valuation analysis is ready for review
                          </Typography>

                          {/* Analysis Period Indicator */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                            <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 0.5 }}>
                              Analysis Period
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {yearRange.start} - {yearRange.end} ({yearRange.end - yearRange.start + 1} years)
                            </Typography>
                          </Box>

                          {/* Key Insights */}
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Action Items & Next Steps
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="body2">
                                All valuation steps completed
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="body2">
                                Your valuation analysis is ready for review
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AssessmentIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="body2">
                                Ready to export or share results
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Quick Actions Panel */}
                      <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 3 }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            Quick Actions
                          </Typography>

                          <Stack spacing={2}>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<AssessmentIcon />}
                              onClick={() => setCurrentTab(5)}
                              fullWidth
                            >
                              View Summary
                            </Button>

                            <Button
                              variant="outlined"
                              size="medium"
                              startIcon={<CalculateIcon />}
                              onClick={() => setCurrentTab(2)}
                              fullWidth
                            >
                              Adjust Methods
                            </Button>

                            <Button
                              variant="outlined"
                              size="medium"
                              startIcon={<AnalyticsIcon />}
                              onClick={() => setCurrentTab(1)}
                              fullWidth
                            >
                              Review Data
                            </Button>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Data Summary Section */}
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Data Summary
                          </Typography>
                          <Grid container spacing={4}>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" fontWeight={700} color="primary.main">
                                  {Object.keys(valuationData.methods).length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Data Metrics
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" fontWeight={700} color="success.main">
                                  5
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Years
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" fontWeight={700} color="warning.main">
                                  0
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Documents
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" fontWeight={700} color="info.main">
                                  {Object.keys(valuationData.methods).length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Sources
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading valuation data...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {currentTab === 1 && (
                <FinancialDataOverview
                  financialData={valuationData?.financialData}
                  dataQuality={valuationData?.dataQuality}
                  yearRange={yearRange}
                  onYearRangeChange={setYearRange}
                />
              )}

              {currentTab === 2 && (
                <ValuationMethodsPanel
                  methods={valuationData?.methods || {}}
                  onMethodToggle={() => {}}
                />
              )}

              {currentTab === 3 && (
                <ValuationAssumptionsPanel
                  assumptions={valuationData?.assumptions}
                  customAssumptions={customAssumptions}
                  onChange={handleAssumptionChange}
                  onRecalculate={handleRecalculate}
                  loading={loading}
                />
              )}

              {currentTab === 4 && (
                <ValuationResultsPanel
                  methods={valuationData?.methods || {}}
                  summary={valuationData?.summary}
                />
              )}

              {currentTab === 5 && (
                <ValuationSummaryPanel
                  data={valuationData}
                  hotelName={hotelName}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ValuationAnalysisPage;